import React, { useState, useEffect } from 'react';
import { useParams, Link, Navigate } from 'react-router-dom';
import { ArrowLeft, ArrowRight, Check, Play, X } from 'lucide-react';
import CTASection from '../components/CTASection';
import useSEO from '../hooks/useSEO';
import { getCaseStudy, CASE_STUDIES, caseStudySchema } from '../caseStudies';

const CaseStudy = () => {
  const { slug } = useParams();
  const cs = getCaseStudy(slug);
  const [lightbox, setLightbox] = useState(null);

  useSEO({
    title: cs ? `${cs.title}, Case Study | Yellow` : 'Case Study | Yellow',
    description: cs?.summary || 'Case study by Yellow, marketing agency Liverpool.',
    path: `/case-studies/${slug}`,
    image: cs?.hero
  });

  useEffect(() => {
    if (!cs) return undefined;
    document.getElementById('case-study-jsonld')?.remove();
    const script = document.createElement('script');
    script.id = 'case-study-jsonld';
    script.type = 'application/ld+json';
    script.text = JSON.stringify(caseStudySchema(cs));
    document.head.appendChild(script);
    return () => script.remove();
  }, [cs]);

  if (!cs) return <Navigate to="/case-studies" replace />;

  const paragraphs = (t) => (t || '').split('\n').filter(Boolean);
  const videos = cs.videos || [];

  return (
    <div className="bg-[#0b0b0d]" data-testid="case-study-detail-page">
      {/* HERO */}
      <section className="relative min-h-[60vh] flex items-end" data-testid="case-study-hero">
        <div className="absolute inset-0 z-0">
          <img
            src={cs.hero}
            alt={`${cs.title}, ${cs.client} case study by Yellow, marketing agency Liverpool`}
            className="w-full h-full object-cover"
            style={{ objectPosition: 'center top' }}
          />
          <div className="absolute inset-0 bg-gradient-to-t from-[#0b0b0d] via-[#0b0b0d]/60 to-[#0b0b0d]/20"></div>
        </div>
        <div className="max-w-[1440px] mx-auto px-6 lg:px-10 relative z-10 pb-14 pt-32 w-full">
          <Link
            to="/case-studies"
            data-testid="back-to-case-studies"
            className="inline-flex items-center gap-2 text-white/70 hover:text-white transition-colors mb-6"
          >
            <ArrowLeft size={18} /> Back to Case Studies
          </Link>
          <div className="max-w-4xl">
            <span
              className="inline-block px-4 py-1.5 text-xs font-semibold uppercase tracking-widest rounded-sm mb-4"
              style={{ backgroundColor: 'rgba(245, 205, 27, 0.14)', color: '#F5CD1B' }}
            >
              {cs.categoryLabel}
            </span>
            <h1 className="font-display text-4xl md:text-5xl lg:text-6xl tracking-tight leading-[1.05] text-white mb-4">
              {cs.title}
            </h1>
            <div className="flex flex-wrap items-center gap-4 text-white/70">
              <span className="font-semibold text-white text-lg">{cs.client}</span>
              <span className="w-1.5 h-1.5 bg-[#F5CD1B] rounded-full"></span>
              <span>{cs.sector}</span>
            </div>
          </div>
        </div>
      </section>

      {/* SUMMARY + CHALLENGE/SOLUTION BOXES */}
      <section className="py-10 lg:py-12 bg-[#0f0f11]" data-testid="case-study-content">
        <div className="max-w-[1440px] mx-auto px-6 lg:px-10">
          <div className="max-w-4xl mb-10">
            <p className="text-xl md:text-2xl text-white leading-relaxed">
              {cs.summary}
            </p>
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="p-6 md:p-7 bg-[#151519] rounded-lg border-l-4 border-[#F5CD1B]">
              <h2 className="font-display text-xl md:text-2xl text-[#F5CD1B] mb-4 uppercase tracking-wide">
                The Challenge
              </h2>
              <div className="space-y-4">
                {paragraphs(cs.challenge).map((p, i) => (
                  <p key={i} className="text-white/70 leading-relaxed">
                    {p}
                  </p>
                ))}
              </div>
            </div>
            <div className="p-6 md:p-7 bg-[#151519] rounded-lg border-l-4 border-[#F5CD1B]">
              <h2 className="font-display text-xl md:text-2xl text-[#F5CD1B] mb-4 uppercase tracking-wide">
                The Solution
              </h2>
              <div className="space-y-4">
                {paragraphs(cs.solution).map((p, i) => (
                  <p key={i} className="text-white/70 leading-relaxed">
                    {p}
                  </p>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* RESULTS + SERVICES */}
      <section className="py-10 lg:py-14 bg-[#0b0b0d]" data-testid="case-study-results">
        <div className="max-w-[1440px] mx-auto px-6 lg:px-10">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-10">
            <div className="lg:col-span-2">
              <h2 className="font-display text-2xl md:text-3xl text-white mb-6 uppercase tracking-wide">
                The Results
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {cs.results.map((r, i) => (
                  <div
                    key={i}
                    data-testid={`result-${i}`}
                    className="flex items-start gap-3 p-4 bg-[#151519] rounded-lg border border-white/5"
                  >
                    <div
                      className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0"
                      style={{ backgroundColor: 'rgba(245, 205, 27, 0.14)' }}
                    >
                      <Check size={16} style={{ color: '#F5CD1B' }} />
                    </div>
                    <p className="text-white text-sm leading-relaxed">{r}</p>
                  </div>
                ))}
              </div>
            </div>
            <div>
              <h2 className="font-display text-2xl md:text-3xl text-white mb-6 uppercase tracking-wide">
                Services
              </h2>
              <div className="flex flex-wrap gap-2">
                {cs.services.map((s, i) => (
                  <span
                    key={s}
                    data-testid={`service-${i}`}
                    className="px-4 py-2 bg-[#1c1c21] text-white text-sm rounded-sm font-medium"
                  >
                    {s}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* PROJECT VIDEOS */}
      {videos.length > 0 && (
        <section className="py-10 lg:py-14 bg-[#0f0f11]" data-testid="case-study-videos">
          <div className="max-w-[1440px] mx-auto px-6 lg:px-10">
            <h2 className="font-display text-2xl md:text-3xl text-white mb-6 uppercase tracking-wide">
              Project Videos
            </h2>
            <div className={`grid gap-6 ${
              videos.length === 1 || videos.length === 3
                ? 'grid-cols-1 max-w-5xl mx-auto'
                : 'grid-cols-1 md:grid-cols-2'
            }`}>
              {videos.map((v, i) => {
                if (!v || !v.id) {
                  return (
                    <div
                      key={i}
                      className="aspect-video bg-[#151519] rounded-lg overflow-hidden border border-dashed border-white/10 flex items-center justify-center"
                      data-testid={`video-embed-${i}`}
                    >
                      <div className="text-center px-6">
                        <div className="w-14 h-14 rounded-full mx-auto mb-3 flex items-center justify-center" style={{ backgroundColor: 'rgba(245, 205, 27, 0.12)' }}>
                          <Play size={22} style={{ color: '#F5CD1B' }} fill="currentColor" />
                        </div>
                        <p className="text-white/60 text-sm">Video coming soon</p>
                      </div>
                    </div>
                  );
                }
                const src =
                  v.type === 'youtube'
                    ? `https://www.youtube.com/embed/${v.id}?rel=0`
                    : `https://player.vimeo.com/video/${v.id}`;
                return (
                  <div
                    key={i}
                    className="aspect-video bg-[#151519] rounded-lg overflow-hidden border border-white/5"
                    data-testid={`video-embed-${i}`}
                  >
                    <iframe
                      src={src}
                      className="w-full h-full"
                      frameBorder="0"
                      allow="autoplay; fullscreen; picture-in-picture"
                      allowFullScreen
                      title={`${cs.title} video ${i + 1}`}
                    />
                  </div>
                );
              })}
            </div>
          </div>
        </section>
      )}

      {/* PROJECT GALLERY */}
      {cs.gallery && cs.gallery.length > 0 && (
        <section className="py-10 lg:py-14 bg-[#0f0f11]" data-testid="case-study-gallery">
          <div className="max-w-[1440px] mx-auto px-6 lg:px-10">
            <h2 className="font-display text-2xl md:text-3xl text-white mb-6 uppercase tracking-wide">
              Project Gallery
            </h2>
            <div className={`grid gap-3 ${
              cs.gallery.length === 4
                ? 'grid-cols-1 sm:grid-cols-2'
                : cs.gallery.length === 2
                ? 'grid-cols-1 sm:grid-cols-2'
                : cs.gallery.length === 1
                ? 'grid-cols-1'
                : 'grid-cols-1 sm:grid-cols-2 md:grid-cols-3'
            }`}>
              {cs.gallery.map((src, i) => (
                <button
                  key={i}
                  onClick={() => setLightbox(src)}
                  data-testid={`gallery-image-${i}`}
                  className="aspect-video bg-[#151519] rounded-lg overflow-hidden group cursor-pointer"
                >
                  <img
                    src={src}
                    alt={`${cs.title} gallery ${i + 1}, by Yellow, marketing agency Liverpool`}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                    loading="lazy"
                  />
                </button>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* PER-PROJECT SECTIONS (Website mocks, ad designs, etc.) */}
      {cs.sections && cs.sections.length > 0 && (
        <section className="py-10 lg:py-14 bg-[#0b0b0d]">
          <div className="max-w-[1440px] mx-auto px-6 lg:px-10 space-y-14">
            {cs.sections.map((sec, i) => (
              <div key={i}>
                <h3 className="font-display text-2xl md:text-3xl text-white mb-6 uppercase tracking-wide">
                  {sec.heading}
                </h3>
                {sec.body && (
                  <p className="text-white/70 text-base lg:text-lg leading-relaxed max-w-3xl mb-8">
                    {sec.body}
                  </p>
                )}
                {sec.images && sec.images.length > 0 && (
                  <div className={`grid gap-3 ${
                    sec.stacked
                      ? 'grid-cols-1 max-w-5xl mx-auto'
                      : sec.images.length === 1
                      ? 'grid-cols-1 max-w-3xl mx-auto'
                      : sec.images.length === 2
                      ? 'grid-cols-1 md:grid-cols-2'
                      : 'grid-cols-1 sm:grid-cols-2 md:grid-cols-3'
                  }`}>
                    {sec.images.map((src, j) => (
                      <button
                        key={j}
                        onClick={() => setLightbox(src)}
                        className="bg-[#151519] rounded-lg overflow-hidden group cursor-pointer flex items-center justify-center"
                      >
                        <img
                          src={src}
                          alt={`${cs.title}, ${sec.heading} ${j + 1} by Yellow, marketing agency Liverpool`}
                          className="w-full h-auto object-contain group-hover:scale-[1.02] transition-transform duration-300"
                          loading="lazy"
                        />
                      </button>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        </section>
      )}

      {/* WANT RESULTS LIKE THESE CTA */}
      <section className="py-14 lg:py-16 bg-[#0b0b0d] relative overflow-hidden" data-testid="case-study-cta">
        <div className="absolute inset-0 opacity-20 pointer-events-none">
          <div
            className="absolute bottom-0 right-0 w-72 h-72 rounded-full blur-[120px]"
            style={{ backgroundColor: '#F5CD1B' }}
          ></div>
        </div>
        <div className="max-w-[1440px] mx-auto px-6 lg:px-10 relative z-10">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="font-display text-3xl md:text-4xl tracking-wide text-white mb-4 uppercase">
              Want Results Like These?
            </h2>
            <p className="text-white/70 mb-8 text-lg">
              Let's discuss how we can create a success story for your business too.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href="#proposal"
                data-testid="case-study-cta-btn"
                className="btn-yellow inline-flex items-center justify-center gap-2 px-8 py-4 rounded-md text-sm uppercase tracking-widest font-bold"
              >
                <Play size={16} fill="currentColor" />
                Book a Call
              </a>
              <Link
                to="/case-studies"
                className="inline-flex items-center justify-center gap-2 px-8 py-4 rounded-md text-sm uppercase tracking-widest font-medium bg-white/10 hover:bg-white/20 border border-white/10 text-white transition-colors"
              >
                View More Work
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Contextual QuoteForm CTA */}
      <CTASection />

      {/* Lightbox */}
      {lightbox && (
        <div
          className="fixed inset-0 z-[100] bg-black/90 backdrop-blur-sm flex items-center justify-center p-4"
          onClick={() => setLightbox(null)}
        >
          <button
            className="absolute top-6 right-6 text-white p-2 hover:text-[#F5CD1B]"
            onClick={() => setLightbox(null)}
            aria-label="Close"
          >
            <X size={28} />
          </button>
          <div className="max-w-5xl w-full" onClick={(e) => e.stopPropagation()}>
            <img src={lightbox} alt="Case study large view" className="w-full h-auto max-h-[85vh] object-contain rounded-xl" />
          </div>
        </div>
      )}
    </div>
  );
};

export default CaseStudy;
